
import ReactMarkdown from 'react-markdown';
import React, { useState, useRef, useEffect } from 'react';
import { Send, X } from 'lucide-react';
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import useLocalization from '../../../../hooks/useLocalization';


import {
    setInputValue,
} from "../../../../store/features/ui/uiSlice";
import { updateFlowStates } from '../../../../store/features/message/updateFlowStates';



const BotResponseText = ({ text, onSendSelection }) => {
    const { t } = useLocalization();
    const dispatch = useDispatch();
    const [selectedText, setSelectedText] = useState('');
    const [showMenu, setShowMenu] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const containerRef = useRef(null);
    const menuRef = useRef(null);


    // input states from Redux


    const formatText = (txt) => txt.trim().replace(/(\d+\.\s)/g, '\n$1');

    // Handle text selection
    const handleMouseUp = () => {
        const selection = window.getSelection();
        const selectedTextContent = selection.toString().trim();

        if (selectedTextContent && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            const containerRect = containerRef.current?.getBoundingClientRect();

            if (containerRect && rect.width > 0) {
                // Check if selection is within our component
                const isWithinContainer = containerRef.current?.contains(range.commonAncestorContainer);

                if (isWithinContainer) {
                    setSelectedText(selectedTextContent);
                    setMenuPosition({
                        x: rect.left + (rect.width / 2) - (containerRect?.left || 0),
                        y: rect.bottom - (containerRect?.top || 0) + 8
                    });
                    setShowMenu(true);
                }
            }
        } else {
            setShowMenu(false);
            setSelectedText('');
        }
    };

    // Handle click outside to close menu
    const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            setShowMenu(false);
            setSelectedText('');
            // Clear selection
            if (window.getSelection) {
                window.getSelection().removeAllRanges();
            }
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Handle send selected text
    const handleSendSelection = () => {
        if (selectedText.trim()) {

            dispatch(setInputValue(selectedText));
            dispatch(updateFlowStates());
            // If onSendSelection callback is provided, call it
            if (onSendSelection) {
                onSendSelection(selectedText);
            }

            // Close menu and clear selection
            setShowMenu(false);
            setSelectedText('');

            // Clear text selection visually
            if (window.getSelection) {
                window.getSelection().removeAllRanges();
            }
        }
    };

    // Handle close menu
    const handleCloseMenu = () => {
        setShowMenu(false);
        setSelectedText('');
        // Clear selection
        if (window.getSelection) {
            window.getSelection().removeAllRanges();
        }
    };

    if (!text) return null;

    return (
        <div
            ref={containerRef}
            className="relative leading-relaxed text-sm"
            onMouseUp={handleMouseUp}
        >
            {/* Main content with custom selection styling */}
            <div className="select-text selection:bg-blue-500 selection:text-white">
                <ReactMarkdown>{formatText(text)}</ReactMarkdown>
            </div>

            {/* Floating Selection Menu */}
            {showMenu && selectedText && (
                <div
                    ref={menuRef}
                    className="absolute z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl py-2 px-1 min-w-max"
                    style={{
                        left: `${menuPosition.x}px`,
                        top: `${menuPosition.y}px`,
                        transform: 'translateX(-50%)'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Selected text preview */}
                    <div className="px-3 py-2 max-w-xs">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            {t('selectedText')}
                        </div>
                        <div className="text-sm text-gray-800 dark:text-gray-200 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded border-l-2 border-blue-500 line-clamp-2">
                            {selectedText}
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center justify-center space-x-2 px-3 py-2 border-t border-gray-100 dark:border-gray-700">
                        <button
                            onClick={handleSendSelection}
                            className="flex items-center space-x-2 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                            title="Send selected text"
                        >
                            <Send size={14} />
                            <span>{t('send')}</span>
                        </button>

                        <button
                            onClick={handleCloseMenu}
                            className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none"
                            title="Close menu"
                        >
                            <X size={14} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

BotResponseText.propTypes = {
    text: PropTypes.string.isRequired,
    onSendSelection: PropTypes.func,
};

export default BotResponseText;